import * as L from 'leaflet';
import 'leaflet.markercluster';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter, Inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { DataSource } from '../../interfaces/data-source.interface';
import { DataSourceConfig } from '../../interfaces/data-source-config.interface';
import { MarkerClusterGroup } from 'leaflet.markercluster';
import { MatDialog } from '@angular/material';
import { NavigationAction } from '../table/interfaces/screen-config-action.interface';
import { SellionsData } from '../../interfaces/sellions-data.interface';
import { Utils } from '../../utils/utils.class';
import { ScreenActions } from '../../interfaces/screen-actions.interface';
import { ComponentWithNavigationAction, ComponentWithProcessAction } from '../../interfaces/component-actions.interface';
import { ProcessAction } from '../../interfaces/process-action.interface';
import { debounceTime, distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';
import { ObservableWorkaround } from '../../utils/observable-workaround.class';
import {MapsService} from "../../services/maps.service";
import { FiltersService } from '../../services/filters/filters.service';
import { FilterSet } from '../../interfaces/filter-set.interface';
import { Subject } from 'rxjs';
import { Filter } from '../table/interfaces/filter.interface';
import {MatIconModule} from '@angular/material/icon';
import { FilterOutput } from '../../interfaces/filter-outpt.interface';
import { DataViewDataSource } from '../../services/data-view-data-source/data-view-data-source.class';
import { CreatorDataSource } from '../../data-sources/creator-data-source';
import { CustomDataSource } from '../../data-sources/custom-data-source';
import { DataService } from '../../services/data/data.service';
import { FilterQueryValue } from '../../interfaces/filter-query-value.inteface';

@Component({
  selector: 'sellions-layout-renderer-map',
  templateUrl: './map.component.html',
  providers: [FiltersService]
})
export class MapComponent implements OnInit, OnDestroy, AfterViewInit, ComponentWithNavigationAction, ComponentWithProcessAction {
  @Output() navigationAction: EventEmitter<NavigationAction> = new EventEmitter<NavigationAction>();
  @Output() screenAction: EventEmitter<ProcessAction> = new EventEmitter<ProcessAction>();
  @ViewChild('search') searchbar: ElementRef;

  @Input() searchQuery: string;
  private unsubscribe$ = new Subject<void>();
  public dataReceived: boolean = false;

  public legendOpen = false;
  public map: L.Map;

  screenActions: ScreenActions;

  private currentPosition = {
    latitude: 52.2521016,
    longitude: 21.0380357,
  };
  private markerCluster: MarkerClusterGroup;
  dataSourcesConfig: DataSourceConfig[];
  private dataSources: CustomDataSource[] = [];
  private authToken: string;
  private iconSettings = {
    popupAnchor: [0, -9],
  };
  private filterSet: FilterSet;

  constructor(private dialog: MatDialog,
              private elementRef: ElementRef,
              private injector: Injector,
              private mapsService: MapsService,
              private filtersService: FiltersService,
              private creatorDataSource: CreatorDataSource,
              private dataService: DataService,
              @Inject('env') private env) {
    window.addEventListener('keyboardDidHide', () => {
      if (this.map) {
        this.map.invalidateSize();
      }
    });
  }

  public ngOnInit() {
    this.setDataSources(this.dataSourcesConfig);
    // this.getData();
    if (!this.map) this.createMap();
    this.setGeolocation();
  }

  private createMap() {
    this.map = L.map('map', { attributionControl: false }).setView([this.currentPosition.latitude, this.currentPosition.longitude], 9);
    L.tileLayer('https://maps.visuu.com/light_map/{z}/{x}/{y}.png', {
      maxZoom: 14,
      minZoom: 4,
    }).addTo(this.map);
  }

  private setGeolocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (response) => {
          this.currentPosition.latitude = response.coords.latitude;
          this.currentPosition.longitude = response.coords.longitude;
        },
        (error) => {
          console.warn("Couldn't establish current position. Using default of (52.1, 21.0)");
          this.currentPosition.latitude = 52.1;
          this.currentPosition.longitude = 21.0;
        },
        { timeout: 10000 },
      );
    }
  }

  ngAfterViewInit(): void {
    ObservableWorkaround.fromEvent(this.searchbar.nativeElement, 'keyup')
      .pipe(
        filter((e: KeyboardEvent) => !!e),
        map(() => this.searchbar.nativeElement.value as string),
        debounceTime(150),
        distinctUntilChanged(),
      )
      .subscribe((v) => this.search(v));
  }

  private search(query: string) {
    if (!this.dataSources || (query.length > 0 && query.length < 3)) return;
    this.searchQuery = query;
    this.reloadData();
  }

  public ngOnDestroy() {
    if (this.map) {
      this.map.off();
      this.map.remove();
    }
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private setDataSources(config: DataSourceConfig[]) {
    config.forEach((dataSource: DataSourceConfig) => {

      const translatedConfig = {
        operationType: 'ONLINE',
        offlineStorageKey: 'map',
        size: 3000,
        url: this.getDataUrl(),
        token: this.authToken
      };
      // @ts-ignore
      const service = this.creatorDataSource.create(translatedConfig);
      this.dataSources.push(service);
      service.dataInitialized$.pipe(takeUntil(this.unsubscribe$)).subscribe((status) => {
        if (status) {
          const locations = service.getData();
          if (!this.dataReceived && locations.length > 0) {
            this.filtersService.extractOptionsFromData('map', locations);
            this.dataReceived = true;
          }
          const mappedLocations = locations.map((d) => ({
            ...d,
            config: dataSource,
          }));
          this.addMarkersToCluster(mappedLocations);
        }
      });
    });
    this.filtersService.getFilters('map')
      .then(filterSet => {
        this.filterSet = filterSet;
        this.filtersService.filterSetSubject$.pipe(takeUntil(this.unsubscribe$)).subscribe(
          (filterSet: FilterSet) => {
            if (!filterSet || filterSet.name !== 'map') {
              return;
            }
            this.reloadData();
          }
        );
      })
      // CATCH + THEN TO SIMULATE FINALLY
      .catch((error) => {console.error(error)})
      .then(() => {
        this.reloadData();
      });
  }

  private async getData(): Promise<SellionsData[]> {
    if (!this.dataSources) return Promise.resolve([]);
    const filterSet = this.filtersService.filterSetSubject$.value ? this.filtersService.filterSetSubject$.value : this.filterSet;
    let filtersOutput = filterSet && filterSet.filters;
    let dataUrl = this.getDataUrl(filtersOutput, this.searchQuery)
    const filters = filtersOutput.map((filter) => {
      return { key: filter.column, value: filter.value, type: filter.type } as Filter;
    });

    this.dataSources[0].loadData({
        page: 0,
        size: 3000,
        url: dataUrl,
        token: this.authToken,
        filters: filters,
        searchQuery: this.searchQuery,
      });
  }

  private flattenLocations(locations: SellionsData[][]): SellionsData[] {
    return locations.reduce((accumulator, value) => accumulator.concat(value), []);
  }

  private reloadData() {
    this.getData();
  }

  private addMarkersToCluster(locations: SellionsData[]) {
    if (this.markerCluster) this.map.removeLayer(this.markerCluster);

    this.markerCluster = (L as any).markerClusterGroup();
    locations.forEach((location) => {
      const marker = this.createMarker(location);
      if (marker) {
        this.markerCluster.addLayers(marker);
      }
    });

    if (this.markerCluster.getLayers().length > 0) {
      this.map.addLayer(this.markerCluster);
      this.map.fitBounds(this.markerCluster.getBounds(), { padding: [50, 50] });
    }
  }

  private createMarker(location: SellionsData): any {
    const config = location.config;

    const latitude = Utils.evaluateString(config.params.latitude, location);
    const longitude = Utils.evaluateString(config.params.longitude, location);

    if (longitude === '0' && latitude === '0') return;

    const title = Utils.evaluateString(config.params.title, location);
    const content = Utils.evaluateString(config.params.content, location);
    const directionLink = this.mapsService.getDirectionsLink(latitude, longitude);
    const marker = L.marker([latitude, longitude], {
      icon: L.divIcon({
        className: 'map-icon m' + location.id,
        ...this.iconSettings,
      }),
    })
      .bindPopup(
        this.getPopupHtml(location.id, title, content, directionLink),
      )
      .on('popupopen', this.activeMarkerAndAddDetailsListener(location, config.detailsOnClick.component, config.detailsOnClick.componentData.viewId, config.detailsOnClick.componentData.id))
      .on('popupclose', this.disableMarker(location.id));

    return marker;
  }

  private getPopupHtml(itemId: string | number, title: string, content: string, directionLink: string): string {
    return (
      `
<div id="mapMarker" class="map-popup">
  <p class="map-popup-title">${title}</p>
  <p class="map-popup-content">${content}</p>
  <p class="map-popup-action">
    <a id="detailsLink${itemId}">Zobacz więcej</a>
  </p>
  <p class="map-popup-action">
    <a href="${directionLink}" target="_blank" style="text-decoration:none">Nawiguj</a>
  </p>
</div>`
    );
  }

  private activeMarkerAndAddDetailsListener(location: SellionsData, component: string, viewId: string, idReference: string): Function {
    let componentData;

    if (idReference) {
      const id = Utils.evaluateString(idReference, location);
      componentData = {id};
    } else {
      componentData = {...location};
    }

    const emitOpenDetails = () =>
      this.navigationAction.emit({
        icon: '',
        label: '',
        component: component,
        componentData: { ...componentData, viewId: viewId },
      });

    return () => {
      const marker = this.elementRef.nativeElement.querySelector('.m' + location.id);
      if (marker) {
        marker.classList.add('active');
      } else {
        console.warn(`[MapComponent]: A marker for item with id: ${location.id} not found`);
      }

      const popUpDetailsLink = this.elementRef.nativeElement.querySelector('#detailsLink' + location.id);
      if (popUpDetailsLink) {
        popUpDetailsLink.addEventListener('touchstart', emitOpenDetails);
        popUpDetailsLink.addEventListener('click', emitOpenDetails);
      } else {
        console.warn(`[MapComponent]: A details link of popup for item with id: ${location.id} not found`);
      }
    };
  }

  private disableMarker(locationId: string): Function {
    return () => {
      const marker = this.elementRef.nativeElement.querySelector('.m' + locationId);
      if (marker) {
        marker.classList.remove('active');
      } else {
        console.warn(`[MapComponent]: A marker for item with id: ${locationId} not found`);
      }
    };
  }

  onButtonClick($event: ProcessAction): void {
    this.screenAction.emit($event);
  }

  searchQueryChange(value: any) {
    this.search(value)
  }

  private getDataUrl(filters?: FilterOutput[], searchQuery?: string): string {
    let url = this.dataService.getUrl(this.env.engineUrl, 'api/data-view/' + this.dataSourcesConfig[0].params.dataViewName);
    url = this.addParamsToUrl(url, filters, searchQuery);

    return url;
  }

  private addParamsToUrl(url: string, filters?: FilterOutput[], searchQuery?: string): string {
    if (filters) {
      url += this.generateFilterUrl(filters);
    }

    if (searchQuery) {
      url += this.generateSearchUrl(url, searchQuery);
    }

    return url;
  }

  private generateFilterUrl(filters?: FilterOutput[]): string {
    const queryFilters: FilterQueryValue[] = [];
    filters.forEach((filter) => {
      if (filter.queryValue.value) {
        const sameColumnFilterIndex = queryFilters.findIndex((f) => f.column === filter.column);

        if (sameColumnFilterIndex > -1) {
          if (queryFilters[sameColumnFilterIndex].type === 'COMPOUND') {
            queryFilters[sameColumnFilterIndex].subFilters.push(filter.queryValue);
          } else {
            const sameColumnFilter = queryFilters.splice(sameColumnFilterIndex, 1);
            const compoundFilter = this.createCompoundFilter(...sameColumnFilter, filter.queryValue);
            queryFilters.push(compoundFilter);
          }
        } else {
          queryFilters.push(filter.queryValue);
        }
      }
    });

    if (queryFilters.length < 1) {
      return '';
    }

    return '?filters=' + encodeURIComponent(JSON.stringify({ filters: queryFilters }));
  }

  private createCompoundFilter(...filters: FilterQueryValue[]): FilterQueryValue {
    return {
      column: filters[0].column, // For contact purposes only - the backend ignores this value for the COMPOUND filter
      type: 'COMPOUND',
      subFilters: filters,
    };
  }

  private generateSearchUrl(url: string, searchQuery: string): string {
    if (url.includes('?')) {
      return '&searchQuery=' + searchQuery;
    }

    return '?searchQuery=' + searchQuery;
  }
}



// WEBPACK FOOTER //
// ./src/submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/components/map/map.component.ts